import { KeyboardEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../api";
import SegmentedControl from "../../../common/components/SegmentedControl/SegmentedControl";
import type {
  IEnhancedUser,
  IGetOrgMembersWithPermissionsResponse,
} from "../../team/state/teamInterfaces";

/* eslint-disable no-useless-escape */
const EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/* eslint-enable no-useless-escape */
const SPLIT_REGEX = /[\s,;]+/;

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
}

type Tab = "creator" | "enduser";

const RecipientsComposer = ({ value, onChange }: Props) => {
  const { t } = useTranslation();
  const [creators, setCreators] = useState<IEnhancedUser[]>([]);
  const [endusers, setEndusers] = useState<IEnhancedUser[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<Tab>("creator");
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get<IGetOrgMembersWithPermissionsResponse>(apiUrls.teamForAdmin)
      .then((res) => {
        if (cancelled) return;
        setCreators((res.data?.creators ?? []).filter((u) => !!u.email));
        setEndusers((res.data?.endusers ?? []).filter((u) => !!u.email));
      })
      .catch(() => {
        // Picker stays hidden — chip input still works
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const memberEmails = useMemo(() => {
    const s = new Set<string>();
    creators.forEach((u) => s.add(u.email.toLowerCase()));
    endusers.forEach((u) => s.add(u.email.toLowerCase()));
    return s;
  }, [creators, endusers]);

  const valueLower = useMemo(() => new Set(value.map((v) => v.toLowerCase())), [value]);

  const isChecked = (email: string) => valueLower.has(email.toLowerCase());

  const externalEmails = value.filter((e) => !memberEmails.has(e.toLowerCase()));

  const tabList = activeTab === "creator" ? creators : endusers;
  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? tabList.filter((u) => {
          const name = `${u.firstname || ""} ${u.lastname || ""}`.toLowerCase();
          return name.includes(q) || u.email.toLowerCase().includes(q);
        })
      : tabList.slice();
    return list.sort((a, b) => Number(isChecked(b.email)) - Number(isChecked(a.email)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabList, search, valueLower]);

  const allFilteredSelected =
    filteredMembers.length > 0 && filteredMembers.every((u) => isChecked(u.email));

  const toggleMember = (u: IEnhancedUser) => {
    if (isChecked(u.email)) {
      onChange(value.filter((e) => e.toLowerCase() !== u.email.toLowerCase()));
    } else {
      onChange([...value, u.email]);
    }
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      const toRemove = new Set(filteredMembers.map((u) => u.email.toLowerCase()));
      onChange(value.filter((e) => !toRemove.has(e.toLowerCase())));
    } else {
      const additions = filteredMembers.filter((u) => !isChecked(u.email)).map((u) => u.email);
      onChange(Array.from(new Set([...value, ...additions])));
    }
  };

  const removeEmail = (email: string) => {
    onChange(value.filter((e) => e !== email));
  };

  const commitDraft = () => {
    const tokens = draft.split(SPLIT_REGEX).filter(Boolean);
    if (tokens.length === 0) {
      setDraft("");
      return;
    }
    const valid: string[] = [];
    let anyInvalid = false;
    for (const token of tokens) {
      if (!EMAIL_REGEX.test(token)) {
        anyInvalid = true;
        continue;
      }
      const normalized = token.toLowerCase();
      if (valueLower.has(normalized)) continue;
      if (!valid.some((v) => v.toLowerCase() === normalized)) valid.push(token);
    }
    if (valid.length > 0) onChange([...value, ...valid]);
    if (valid.length === 0 && anyInvalid) {
      setDraftError(true);
      return;
    }
    setDraft("");
    setDraftError(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        commitDraft();
      } else if (e.key !== "Tab") {
        e.preventDefault();
      }
    } else if (e.key === "Backspace" && !draft && externalEmails.length > 0) {
      e.preventDefault();
      removeEmail(externalEmails[externalEmails.length - 1]);
    }
  };

  const hasMembers = creators.length > 0 || endusers.length > 0;
  const showPickerShell = !loaded || hasMembers;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        <span
          className={`text-xs ${value.length > 0 ? "text-green-600 font-medium" : "text-gray-400"}`}
        >
          {t("pages.statistics.reports.fields.recipientsCount", {
            count: value.length,
          })}
        </span>
      </div>

      {showPickerShell && (
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
          <div className="p-2 border-b border-gray-100 flex items-center gap-2">
            <SegmentedControl<Tab>
              size="sm"
              variant="pill"
              value={activeTab}
              onChange={(v) => setActiveTab(v)}
              options={[
                {
                  value: "creator",
                  label: t("pages.statistics.reports.fields.tabCreators"),
                  badge: creators.length,
                },
                {
                  value: "enduser",
                  label: t("pages.statistics.reports.fields.tabEndusers"),
                  badge: endusers.length,
                },
              ]}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("pages.statistics.reports.fields.membersSearchPlaceholder")}
              disabled={!loaded}
              className="flex-1 min-w-0 px-2.5 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400 placeholder-gray-300 disabled:bg-gray-50"
            />
            {loaded && filteredMembers.length > 0 && (
              <button
                type="button"
                onClick={toggleAll}
                className="shrink-0 inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md border border-green-100 transition-colors"
              >
                {allFilteredSelected
                  ? t("pages.statistics.reports.fields.deselectAll", {
                      count: filteredMembers.length,
                    })
                  : t("pages.statistics.reports.fields.selectAll", {
                      count: filteredMembers.length,
                    })}
              </button>
            )}
          </div>
          <div className="h-64 overflow-y-auto">
            {!loaded ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-xs text-gray-400">{t("general.loading")}</p>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-xs text-gray-400">
                  {t("pages.statistics.reports.fields.membersEmpty")}
                </p>
              </div>
            ) : (
              filteredMembers.map((u) => {
                const checked = isChecked(u.email);
                const fullName = `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.email;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleMember(u)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                      checked ? "bg-green-50/60 hover:bg-green-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                        checked ? "bg-green-500 border-green-500" : "border-gray-300"
                      }`}
                    >
                      {checked && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="flex flex-col min-w-0 flex-1">
                      <span className="truncate font-medium text-gray-700">{fullName}</span>
                      <span className="truncate text-gray-400">{u.email}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      <div>
        <div className="text-xs font-medium text-gray-600 mb-1">
          {t("pages.statistics.reports.fields.externalLabel")}
        </div>
        <div className="flex flex-wrap gap-1.5 p-2 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-green-500 focus-within:border-green-500">
          {externalEmails.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-sm"
            >
              {email}
              <button
                type="button"
                onClick={() => removeEmail(email)}
                className="text-green-600 hover:text-green-800 ml-1"
                aria-label={t("pages.statistics.reports.fields.recipientsRemove")}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDraftError(false);
            }}
            onKeyDown={handleKeyDown}
            onBlur={commitDraft}
            placeholder={
              externalEmails.length === 0
                ? t("pages.statistics.reports.fields.recipientsPlaceholder")
                : ""
            }
            className="flex-1 min-w-[180px] px-2 py-1 text-sm outline-none bg-transparent"
          />
        </div>
        {draftError && (
          <p className="mt-1 text-xs text-red-600">
            {t("pages.statistics.reports.fields.recipientsInvalid")}
          </p>
        )}
      </div>
    </div>
  );
};

export default RecipientsComposer;
